import type React from "react"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Upload, FileText, X, Loader2 } from "lucide-react"
import { getYears, SUBJECTS, DIFFICULTY_OPTIONS, formatFileSize } from "@/lib/utils"
import { useTagManager } from "@/lib/hooks/useTagManager"
import { useFileUpload } from "@/lib/hooks/useFileUpload"
import { api, type ExamPaper } from "@/lib/api"
import { toast } from "sonner"
import { parseFilename } from "@/lib/parseFilename"

const years = getYears()
const subjects = SUBJECTS

interface ExamFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  examData?: ExamPaper
  onSuccess?: () => void
}

export function ExamFormDialog({ open, onOpenChange, examData, onSuccess }: ExamFormDialogProps) {
  const isEditMode = !!examData

  const { file, isDragging, setFile, handleFileDrop, handleFileSelect, handleDragOver, handleDragLeave } = useFileUpload()
  const { tags, tagInput, setTagInput, handleAddTag, handleRemoveTag, handleKeyDown } = useTagManager(examData?.tags || [])
  const [title, setTitle] = useState(examData?.title || "")
  const [year, setYear] = useState(examData?.year.toString() || "")
  const [region, setRegion] = useState(examData?.region || "")
  const [subject, setSubject] = useState(examData?.subject || "")
  const [difficulty, setDifficulty] = useState(examData?.difficulty || "")
  const [duration, setDuration] = useState(examData?.duration?.toString() || "120")
  const [suggestion, setSuggestion] = useState(examData?.suggestion || "")
  const [description, setDescription] = useState(examData?.description || "")
  const [isUploading, setIsUploading] = useState(false)

  // Sync form state with examData when it changes
  useEffect(() => {
    if (examData) {
      setTitle(examData.title || "")
      setYear(examData.year?.toString() || "")
      setRegion(examData.region || "")
      setSubject(examData.subject || "")
      setDifficulty(examData.difficulty || "")
      setDuration(examData.duration?.toString() || "120")
      setSuggestion(examData.suggestion || "")
      setDescription(examData.description || "")
    } else {
      // Reset form when opening in create mode
      setTitle("")
      setYear("")
      setRegion("")
      setSubject("")
      setDifficulty("")
      setDuration("120")
      setSuggestion("")
      setDescription("")
    }
  }, [examData])

  // Auto-fill form from filename when file is selected
  useEffect(() => {
    if (file && !isEditMode) {
      const parsed = parseFilename(file.name)

      if (parsed.title) {
        setTitle(parsed.title)
      }
      if (parsed.year) {
        setYear(parsed.year.toString())
      }
      if (parsed.region) {
        setRegion(parsed.region)
      }
      if (parsed.subject) {
        setSubject(parsed.subject)
      }

      if (parsed.year || parsed.region || parsed.subject) {
        toast.success("已自动填充表单")
      }
    }
  }, [file, isEditMode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsUploading(true)

    try {
      if (isEditMode && examData?.id) {
        // Update existing paper
        await api.updatePaper(examData.id, {
          title,
          year: parseInt(year),
          region,
          subject,
          difficulty: difficulty || undefined,
          tags,
          duration: parseInt(duration),
          suggestion: suggestion || undefined,
          description: description || undefined,
        })
        toast.success("试卷更新成功！")
      } else {
        // Create new paper
        if (!file) {
          toast.error("请选择文件")
          return
        }

        const uploadResult = await api.uploadFile(file)
        await api.createPaper({
          title,
          year: parseInt(year),
          region,
          subject,
          difficulty: difficulty || undefined,
          tags,
          duration: parseInt(duration),
          suggestion: suggestion || undefined,
          description: description || undefined,
          file_key: uploadResult.file_key,
          file_size: uploadResult.file_size
        })
        toast.success("试卷上传成功！")
      }

      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error(isEditMode ? "Update error:" : "Upload error:", error)
      toast.error(error instanceof Error ? error.message : (isEditMode ? "更新失败，请重试" : "上传失败，请重试"))
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[95vh] w-[95vw] max-w-2xl p-0 sm:max-h-[90vh] sm:w-full">
        <DialogHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
          <DialogTitle>{isEditMode ? "编辑试卷" : "上传试卷"}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(95vh-60px)] sm:max-h-[calc(90vh-80px)]">
          <form onSubmit={handleSubmit} className="space-y-4 px-4 pb-4 sm:space-y-6 sm:px-6 sm:pb-6">
            {!isEditMode && (
              <div className="space-y-2">
                <Label className="text-sm">文件上传</Label>
                <div
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`relative flex min-h-[160px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors sm:min-h-[180px] ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : file
                        ? "border-border bg-muted/20"
                        : "border-border bg-muted/10 hover:border-primary hover:bg-primary/5"
                  }`}
                >
                  {file ? (
                    <div className="flex flex-col items-center gap-2 px-4 text-center sm:gap-3">
                      <div className="rounded-full bg-primary/10 p-3 sm:p-4">
                        <FileText className="h-6 w-6 text-primary sm:h-8 sm:w-8" />
                      </div>
                      <div>
                        <p className="break-all text-sm font-medium text-foreground">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFile(null)
                        }}
                      >
                        重新选择
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 px-4 text-center sm:gap-3">
                      <div className="rounded-full bg-muted p-3 sm:p-4">
                        <Upload className="h-6 w-6 text-muted-foreground sm:h-8 sm:w-8" />
                      </div>
                      <div>
                        <p className="mb-1 text-xs text-foreground sm:text-sm">拖拽 PDF 文件到这里，或点击选择文件</p>
                        <p className="text-xs text-muted-foreground">支持 .pdf 格式，最大 50MB</p>
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm">
                试卷标题 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder="例：2019年全国II卷-数学"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="year">
                  年份 <span className="text-destructive">*</span>
                </Label>
                <Select value={year} onValueChange={setYear} required>
                  <SelectTrigger id="year">
                    <SelectValue placeholder="选择年份" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}年
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="region">
                  地区 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="region"
                  placeholder="例：全国II卷、北京卷"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">
                科目 <span className="text-destructive">*</span>
              </Label>
              <Select value={subject} onValueChange={setSubject} required>
                <SelectTrigger id="subject">
                  <SelectValue placeholder="选择科目" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">难度</Label>
              <RadioGroup value={difficulty} onValueChange={setDifficulty}>
                <div className="flex flex-wrap gap-4 sm:gap-6">
                  {DIFFICULTY_OPTIONS.map((level) => (
                    <div key={level} className="flex items-center space-x-2">
                      <RadioGroupItem value={level} id={level} />
                      <Label htmlFor={level} className="text-sm font-normal">
                        {level}
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">标签</Label>
              <div className="space-y-3">
                <Input
                  id="tags"
                  placeholder="添加标签，如：函数、导数"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleAddTag}
                />
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="gap-1 pr-1 bg-primary/10 text-primary">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="rounded-full p-0.5 hover:bg-primary/20"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">建议用时</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  max="300"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-32"
                />
                <span className="text-sm text-muted-foreground">分钟</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">试卷简介</Label>
              <Textarea
                id="description"
                placeholder="试卷的基本介绍和说明"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="suggestion">做题建议</Label>
              <Textarea
                id="suggestion"
                placeholder="给学生的做题建议和注意事项"
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>

            <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-between">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
                取消
              </Button>
              <Button
                type="submit"
                disabled={isUploading || (!isEditMode && !file) || !title || !year || !region || !subject}
                className="w-full sm:w-auto"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditMode ? "保存中..." : "上传中..."}
                  </>
                ) : isEditMode ? (
                  "保存修改"
                ) : (
                  "上传试卷"
                )}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
